import { FilterSelect } from '@/features/common/FilterPanel/FilterSelect';
import { domainLabel } from '@/features/common/useThematicDomains';
import { ListingFilterPanel } from '@/features/publicsite/ListingFilterPanel';
import { activeFilterTags } from '@/features/common/listingFilters';
import { eventFilters } from '@/features/publicsite/events/eventFilters';
import { EVENT_KIND_LABEL, EVENT_KINDS } from '@/features/publicsite/events/eventKind';
import type { EventFilterPanelProps } from '@/features/publicsite/types/events/EventFilterPanelProps';
import { publicEvents, type PublicEventsKey } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';

/** The lists that get a tag under the card when set, in the order the card shows them. */
const TAGGED = ['region', 'theme', 'kind', 'organizer'] as const;

/** What each list reads while nothing is picked — and its label is hidden, so it names the list. */
const ALL_LABEL: Record<(typeof TAGGED)[number], PublicEventsKey> = {
  region: 'filterAllRegions',
  theme: 'filterAllThemes',
  kind: 'filterAllKinds',
  organizer: 'filterAllOrganizers',
};

/**
 * The calendar's filter card: the shared listing card /stiri has (ListingFilterPanel), opening on
 * the search and the period alone.
 *
 * Two controls in the row, not six. The four lists tor.md §6.11 names — "filtrare avansată după
 * regiune, tematică, tip de activitate și organizator" — are advanced filtering, and that is what
 * "Mai multe filtre" is: the raion and the theme stood in the open row beside the search, so a page
 * whose job is to show a calendar opened on a bar of dropdowns instead. They open on their own when
 * one of them is set, so a shared link with ?kind= shows the field it narrows by, and each one set
 * shows as a tag under the card either way.
 *
 * `periodInRow`: the period is the other thing a visitor comes to this page to set, so it keeps its
 * place beside the search rather than folding with the lists. One field — the range control the
 * other listings use, emptied by its own "Golește perioada" — not a pair of date boxes.
 *
 * The four lists stay in the URL and keep their tags whether the block is open or not: they are
 * tor.md §6.11's "filtrare avansată", a contractual filter each, and folding them is where they are
 * drawn, not whether they work.
 */
export function EventFilterPanel({
  params,
  regions,
  themeOptions,
  organizerOptions,
  results,
  onFilter,
  onReset,
}: EventFilterPanelProps) {
  const { t, language } = useTranslation(publicEvents);
  const filters = eventFilters(params);
  // The four lists that fold, and how many of them are narrowing the calendar: the button says so,
  // since a filter nobody can see is one nobody can take off.
  const folded = TAGGED.filter((name) => filters[name]).length;
  // Each list's choices once: its dropdown offers them, and its tag names the one picked.
  const choices = {
    region: regions.map((r) => ({ value: r.id, label: r.name })),
    theme: themeOptions.map((d) => ({ value: d.id, label: domainLabel(d, language) })),
    kind: EVENT_KINDS.map((k) => ({ value: k, label: t(EVENT_KIND_LABEL[k]) })),
    organizer: organizerOptions.map((o) => ({ value: o.id, label: o.name })),
  };
  // What every list passes the same way, so each FilterSelect below shows only what differs.
  const wiring = (name: (typeof TAGGED)[number]) => ({
    name,
    placeholder: t(ALL_LABEL[name]),
    value: filters[name] ?? '',
    options: [{ value: '', label: t(ALL_LABEL[name]) }, ...choices[name]],
    onChange: (value: string) => onFilter({ [name]: value }),
    labelHidden: true,
  });
  const tags = activeFilterTags(TAGGED, filters, choices).map((tag) => ({
    ...tag,
    onRemove: () => onFilter({ [tag.name]: null }),
  }));

  return (
    <ListingFilterPanel
      ariaLabel={t('filtersAria')}
      filters={filters}
      canReset={Object.keys(filters).length > 0}
      results={results}
      onFilter={onFilter}
      onReset={onReset}
      variant="plain"
      periodInRow
      tags={tags}
      moreOpen={folded > 0}
      moreCount={folded}
      more={
        <>
          <FilterSelect
            {...wiring('region')}
            label={t('filterRegion')}
            ariaLabel={t('filterRegionAria')}
          />
          <FilterSelect
            {...wiring('theme')}
            label={t('filterTheme')}
            ariaLabel={t('filterThemeAria')}
          />
          <FilterSelect
            {...wiring('kind')}
            label={t('filterKind')}
            ariaLabel={t('filterKindAria')}
          />
          <FilterSelect
            {...wiring('organizer')}
            label={t('filterOrganizer')}
            ariaLabel={t('filterOrganizerAria')}
            // Off only when there is nothing to choose and nothing to take off, as /stiri's category.
            disabled={organizerOptions.length === 0 && !filters.organizer}
          />
        </>
      }
    />
  );
}
